import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";

import { LorenRole } from "../interfaces/loren-role.interface";
import { GenericItems } from "../../../shared/interfaces/generic-items.interface";
import { LorenRoleEndpointsService } from "./loren-role-endpoints.service";
import { RefreshSubject } from "../../../common/rxjs/RefreshSubject";

@Injectable({
  providedIn: "root",
})
export class LorenRoleService {
  private http = inject(HttpClient);
  private endpoints = inject(LorenRoleEndpointsService);

  selectedRole$ = new BehaviorSubject<LorenRole>(null);
  refresh$ = new RefreshSubject();
  allRoles$ = this.refresh$.pipe(
    switchMap(() =>
      this.http
        .get<GenericItems<LorenRole>>(this.endpoints.roles())
        .pipe(map((x) => x.items.sort((a, b) => b.id - a.id)))
    )
  );

  clearSelectedRoles = () => {
    return this.selectedRole$.next(null);
  };

  createRole$ = (role: LorenRole) => {
    return this.http
      .post<LorenRole>(this.endpoints.roles(), role)
      .pipe(tap(() => this.refresh$.next()));
  };

  updateRole$ = (role: LorenRole) => {
    return this.http
      .put<LorenRole>(this.endpoints.userRolesById(role.id), role)
      .pipe(tap(() => this.refresh$.next()));
  };

  deleteRole$ = (roleId: number) => {
    return this.http
      .delete<LorenRole>(`${this.endpoints.userRolesById(roleId)}`)
      .pipe(tap(() => this.refresh$.next()));
  };

  getUserRoles$ = (id: string) => {
    return this.http
      .get<GenericItems<LorenRole>>(this.endpoints.userRoles(Number(id)))
      .pipe(map((x) => x.items));
  };

  updateUserRoles$ = (id: string, roles: number[]) => {
    return this.http.put<void>(this.endpoints.userRoles(Number(id)), roles);
  };
}
